/**
 * SubscribeToChatNodesUseCase
 *
 * Live-update use case for the inbox direct-messages list. Wraps the live
 * subscription on the `message_nodes` collection (filtered to the current
 * user's conversations) with per-change E2EE decryption of `text`-type rows'
 * `lastContent`, then pipes the decrypted changes to the caller. The hook
 * (Task 9) owns the add/modify/remove list mutation + sort; this use case is
 * the decrypt + pipe.
 *
 * Flutter source: lib/presentation/screens/messages/messages_screen.dart:459-519
 * (`_subscribeToChatUpdates`).
 *   - Query/conditional cursor handled by `ChatService.subscribeToChatNodes`
 *     (over `firestoreService.subscribeToChatNodes`). `sinceTimestamp` is
 *     forwarded UNCHANGED: a numeric value yields the `>=` filter (non-empty
 *     first page → `_mostRecentChatDocument != null`, lines 466-473); a
 *     `null`/`undefined` value yields the unfiltered subscription (empty first
 *     page → `_mostRecentChatDocument == null`, lines 474-475).
 *   - messages_screen.dart:506-519 — for each `text`-type change ONLY, the peer
 *     is resolved (`peerUserId = _myUser.firebaseId == node.idFrom ? node.idTo
 *     : node.idFrom`), the peer `UserEntity` is fetched, and IF the peer exists
 *     the E2EE key is derived from `peerUser.publicJwk` and the change's
 *     `lastContent` is decrypted in place. Non-`text` changes pass through
 *     untouched. We mirror this exact ordering: resolve peer → `GetUserUseCase`
 *     → `InitializeTextEncryptionUseCase` → `DecryptTextUseCase`.
 *
 * Pure-domain contract:
 *   - The use case owns the peer-resolve + decrypt; the service stays a thin
 *     subscribe + remap. The decrypt trio is invoked exactly as
 *     `FetchChatNodesUseCase` does: the singleton's AES-GCM key is re-derived
 *     per peer via `InitializeTextEncryptionUseCase` immediately before each
 *     `DecryptTextUseCase`, so interleaved peers do not clobber one another.
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { ChatService } from '@/domain/services/chat/chatService';
import { GetUserUseCase, getUserUseCase } from '@/domain/usecases/user/getUserUseCase';
import {
  InitializeTextEncryptionUseCase,
  initializeTextEncryptionUseCase,
} from '@/domain/usecases/textEncryption/initializeTextEncryptionUseCase';
import {
  DecryptTextUseCase,
  decryptTextUseCase,
} from '@/domain/usecases/textEncryption/decryptTextUseCase';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import type { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { ChatUtils } from '@/domain/utils/chatUtils';

/**
 * A single live chat-node change delivered to the caller: the Firestore change
 * type plus the (already peer-decrypted, for `text` rows) entity.
 */
export interface ChatNodeChange {
  type: RemoteDocumentChangeType;
  data: ChatMessageNodeEntity;
}

export class SubscribeToChatNodesUseCase {
  private chatService: ChatService;
  private getUserUseCase: GetUserUseCase;
  private initializeTextEncryptionUseCase: InitializeTextEncryptionUseCase;
  private decryptTextUseCase: DecryptTextUseCase;

  constructor(
    chatService: ChatService = ChatService.getInstance(),
    getUser: GetUserUseCase = getUserUseCase,
    initializeEncryption: InitializeTextEncryptionUseCase = initializeTextEncryptionUseCase,
    decryptUseCase: DecryptTextUseCase = decryptTextUseCase,
  ) {
    this.chatService = chatService;
    this.getUserUseCase = getUser;
    this.initializeTextEncryptionUseCase = initializeEncryption;
    this.decryptTextUseCase = decryptUseCase;
  }

  /**
   * Subscribe to live chat-node updates for `userId`'s inbox. For each
   * delivered batch, every `text`-type change's `lastContent` is decrypted in
   * place (non-`text` changes pass through), then the batch is handed to
   * `onChanges`. The list mutation/sort lives in the consuming hook (Task 9).
   *
   * @param userId - The current user's Firebase id (used both as the query's
   *   `array-contains` member and to resolve the peer side of each change).
   * @param sinceTimestamp - The most-recent chat timestamp from the first page,
   *   or `null`/`undefined` when the first page was empty. Forwarded UNCHANGED
   *   to the service: numeric → `>=` filter, `null`/`undefined` → unfiltered
   *   subscription (messages_screen.dart:466-475).
   * @param onChanges - Called once per snapshot batch with the array of
   *   (decrypted) changes.
   * @returns The Firestore `Unsubscribe` handle from
   *   `ChatService.subscribeToChatNodes`. The caller (the feature hook) is
   *   responsible for invoking it on cleanup.
   */
  execute(
    userId: string,
    sinceTimestamp: number | null | undefined,
    onChanges: (changes: ChatNodeChange[]) => void,
  ): Unsubscribe {
    return this.chatService.subscribeToChatNodes(
      userId,
      sinceTimestamp,
      (changes) => {
        void this.processChanges(userId, changes, onChanges);
      },
    );
  }

  /**
   * Decrypt each `text`-type change's `lastContent` in place, then deliver the
   * whole batch to `onChanges`. Serial — Flutter decrypts serially inside the
   * `docChanges` for-loop (messages_screen.dart:481-519), and serialization is
   * REQUIRED: the encryption singleton has a single derived-key slot, so the
   * initialize→decrypt pair for one peer must complete before the next peer's
   * initialize re-derives the key (parity with `FetchChatNodesUseCase`).
   *
   * The decrypt step writes the result to the entity's camelCase `lastContent`
   * (NOT the DTO's `last_content`), consistent with the entity surface this
   * feature exposes.
   */
  private async processChanges(
    userId: string,
    changes: ChatNodeChange[],
    onChanges: (changes: ChatNodeChange[]) => void,
  ): Promise<void> {
    for (const change of changes) {
      const node = change.data;

      // Decrypt ONLY a `text`-type change with non-null, non-empty content. The
      // predicate (and the rationale for excluding the share types) lives on
      // `ChatUtils.shouldDecryptNodeContent`, shared with
      // `FetchChatNodesUseCase`.
      if (!ChatUtils.shouldDecryptNodeContent(node.type, node.lastContent)) {
        continue;
      }

      // Resolve the peer: the OTHER user on the node. messages_screen.dart:507-510.
      const peerId = userId === node.idFrom ? node.idTo : node.idFrom;

      // Fetch the peer user; decrypt only if it exists (messages_screen.dart:512-518
      // — `if (peerUser != null) { ... decrypt ... }`).
      const peerUser = await this.getUserUseCase.execute(peerId);
      if (peerUser == null) {
        continue;
      }

      // Re-derive the AES-GCM key for this peer, then decrypt in place.
      await this.initializeTextEncryptionUseCase.execute(
        (peerUser.publicJwk as JsonWebKey | undefined) ?? null,
      );
      node.lastContent = await this.decryptTextUseCase.execute(node.lastContent);
    }

    onChanges(changes);
  }
}

export const subscribeToChatNodesUseCase = new SubscribeToChatNodesUseCase();
